<template>
    <div>
        <v-server-table ref="customFormEntryTable" url="/custom-forms/list" :columns="columns" :options="options">

            <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                <div class="clearfix d-md-none d-block"></div>

                <div class="float-left mr-2">
                    <user-filters ref="customFormsSavedFilters" assetType="CovidFormEntry" :filterKey="filterKey" @saving="setUserFilterData" @show="setUserFilterData"></user-filters>
                </div>

                <div class="float-left mr-2">
                    <table-filters-2
                        ref="customFormTableFilters"
                        filterUrl="/custom-forms/filters"
                        v-bind:filters="filters"
                        :emptyFilters="filterDefs"
                        :filterKey="filterKey"
                        @filtersChanged="refreshTableData"
                        @filtersCleared="filtersCleared"
                        v-show="!dashboardFilters">
                        <template slot-scope="slotProps">
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['covid_form_entries.covid_form_id']" :btnLabel="(values) => ('Forms ('+values.length+')')" :selectOptions="slotProps.filterData.forms" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['covid_form_entries.entry_status']" :btnLabel="(values) => ('Status ('+values.length+')')" :selectOptions="statusOptions" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['covid_form_entries.visitor_or_employee']" :btnLabel="(values) => ('People ('+values.length+')')" :selectOptions="typeOptions" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Entry Date" v-model="slotProps.filters['covid_form_entries.created_at']" />
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['jobsite.DivisionId']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="slotProps.filterData.divisions" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block" style="width: 250px;" v-if="!jobsite_id">
                                <jobsite-select :selectedValues="slotProps.filters['covid_form_entries.jobsite_id']" v-on:vselected="((data) => { slotProps.filters['covid_form_entries.jobsite_id'] = data || {};  })" :forFilters="true" />
                            </div>
                            <div class="d-inline-block" style="width: 250px;" v-if="!employee_id">
                                <employee-select :selectedValues="slotProps.filters['covid_form_entries.employee_id']" v-on:vselected="((data) => { slotProps.filters['covid_form_entries.employee_id'] = data || {};  })" :forFilters="true" />
                            </div>

                            <template v-if="single_form_filters">
                                <hr/>

                                <div class="row" style="max-height: 300px; overflow-y: auto;">
                                    <div class="col-md-3 mb-3">
                                        <div class="form-group">
                                            <label class="d-block">Form Filter Logic:</label>
                                            <b-form-select v-model="slotProps.filters['single_filter_logic']" :options="singleFilterLogicOptions" />
                                        </div>

                                        <div class="form-group">
                                            <label class="d-block">Add Form Filter Field:</label>
                                            <b-form-select v-model="single_filter_add" :options="singleFormFilterOptions" @change="pushSingleFilter" />
                                        </div>
                                    </div>
                                    <div class="col-md-2 mb-3" v-for="(filter, i) in selected_single_form_filters">
                                        <label class="d-block">
                                            <a href="javascript:void(0)" class="text-danger" title="Remove Filter" v-on:click="removeSingleFilter(i)"><i class="fa fa-remove"></i></a> {{ filter.label }}:
                                        </label>
                                        <multi-select
                                            v-if="filter.type === 'Radio' || filter.type === 'Dropdown' || filter.type === 'Checkbox'"
                                            v-model="slotProps.filters['single_form_field_'+filter.id]"
                                            :btnLabel="(values) => ('Options ('+values.length+')')"
                                            :selectOptions="getFormFilterOptions(filter.options)"
                                            :options="{multi: true}" />

                                        <b-form-input
                                            v-if="filter.type === 'Text' || filter.type === 'Textarea'"
                                            v-model="slotProps.filters['single_form_field_'+filter.id]"
                                            :placeholder="`Search ${filter.label}`" />
                                    </div>
                                </div>
                            </template>
                        </template>
                    </table-filters-2>
                </div>

                <button type="button" class="btn btn-outline-success" v-on:click="exportTable()" :disabled="exportingTable" v-if="showExport">
                    <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                    <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                </button>

                <router-link :to="'/custom-forms/entry/create?' + (employee_id ? 'employee_id='+employee_id : '') + (jobsite_id ? 'jobsite_id='+jobsite_id : '')" class="btn btn-success" v-if="showAdd" title="Add a Custom Form"><i class="fa fa-plus-circle"></i> Add</router-link>
            </div>

            <template slot="id" slot-scope="props">
                <router-link :to="`/custom-forms/entry/view/${props.row.id}`" title="View Custom Form" class="btn btn-sm btn-secondary">
                    View
                </router-link>
            </template>

            <template slot="entry_status" slot-scope="props">
                <label :class="'badge badge-'+(props.row.entry_status === 'Open' ? 'danger' : 'success')+''">{{ props.row.entry_status }}</label>
            </template>

            <template slot="person_name" slot-scope="props">
                <router-link v-if="props.row.employee_id" :to="`/employees/view/${props.row.employee_id}`">
                    {{ props.row.FirstName }} {{ props.row.LastName }} - {{ props.row.EmployeeNumber }}
                </router-link>
                <span v-else>{{ props.row.person_name }} <span class="badge badge-secondary">Guest</span></span>
            </template>

            <template slot="created_at" slot-scope="props">
                {{ props.row.created_at | moment("timezone", userTimezone, "MM/DD/YYYY hh:mm a") }}
            </template>

            <template slot="JobsiteName" slot-scope="props">
                <router-link :to="`/jobsites/view/${props.row.jobsite_id}`" v-if="props.row.JobsiteName">
                    {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
                </router-link>
            </template>

            <template slot="signature_file_id" slot-scope="props">
                <a :href="`/files/view/${props.row.signature_file_id}`" target="_blank" title="View Signature" v-if="props.row.signature_file_id"><i class="fa fa-pencil-square"></i> View</a>
            </template>

        </v-server-table>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';

    export default {
        props: {
            employee_id: {
                type: Number,
                default: null,
            },

            jobsite_id: {
                type: Number,
                default: null,
            },

            showExport: {
                type: Boolean,
                default: true,
            },

            showAdd: {
                type: Boolean,
                default: false,
            },

            allowFilterSaving: {
                //type: Boolean,
                default: true,
            },

            filterId: {
                type: Number,
                default: null,
            },

            dashboardFilters: {
                type: Object,
                default(){
                    return null;
                },
            },

            tableConfig: {
                type: Object,
                default(){
                    return null;
                },
            },
        },

        data() {
            return {
                exportingTable: false,
                single_form_filters: null,
                single_form_id: null,
                single_filter_add: null,
                selected_single_form_filters: [],
                userTimezone: this.$store.state.user.timezone,
                columns: ['id', 'custom_form_name', 'entry_status', 'person_name', 'created_at', 'JobsiteName', 'signature_file_id'],
                options: {
                    headings: {
                        id: 'Actions',
                        custom_form_name: 'Form Name',
                        entry_status: 'Status',
                        person_name: 'Employee/Visitor',
                        created_at: 'Date',
                        JobsiteName: 'Jobsite',
                        signature_file_id: 'Signature',
                    },
                    orderBy: {
                        ascending: false,
                        column: 'covid_form_entries.created_at',
                    },
                    sendInitialRequest: (this.filterId ? false : true),
                    responseAdapter: (response) => {
                        //Emit the count
                        this.$emit('customFormListCount', response.data.count);

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Send it
                        data.employee_id = this.employee_id;
                        data.jobsite_id = this.jobsite_id;

                        //Parse the filters
                        //data.filters = filterExtractor.extractFilters(this.filters);
                        data.filters = (this.dashboardFilters ? filterExtractor.extractFilters(this.dashboardFilters) : filterExtractor.extractFilters(this.filters));

                        //Send it
                        return data;
                    },
                    uniqueKey: 'id',
                },

                typeOptions: [
                    {id: 'Visitor', name: 'Guests'},
                    {id: 'Employee', name: 'Employees'},
                ],

                statusOptions: [
                    {id: 'Open', name: 'Open'},
                    {id: 'Closed', name: 'Closed'},
                ],

                filterDefs: {
                    'covid_form_entries.created_at' : null,
                    'covid_form_entries.visitor_or_employee': [],
                    'covid_form_entries.covid_form_id': [],
                    'covid_form_entries.entry_status': [],
                    'jobsite.DivisionId': [],
                    'covid_form_entries.jobsite_id' : {},
                    'covid_form_entries.employee_id' : {},
                    'single_filter_logic': 'and',
                },

                filterKey: 'customFormFilters',

                single_filter_logic: 'and',
                singleFilterLogicOptions: [
                    {value: 'and', text: 'AND'},
                    {value: 'or', text: 'OR'}
                ],
            }
        },

        watch: {
            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {

            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },

            singleFormFilterOptions(){
                //Setup
                if(!this.single_form_id)
                {
                    return [];
                }

                //Setup
                var options = [
                    {value: null, text: '-- Select Additional Filters from Form Fields --'}
                ];

                //loop
                for (var i = 0; i < this.single_form_filters.length; i++)
                {
                    options.push({value: this.single_form_filters[i].id, text: this.single_form_filters[i].label});
                }

                //send it
                return options;
            },
        },

        methods: {

            refreshTableData(filters) {
                //Set the data
                this.$refs.customFormEntryTable.setPage(1);
                //this.$refs.customFormEntryTable.getData();

                //EHS-1348, Single Form Filter Fields
                if(this.filters['covid_form_entries.covid_form_id'].length === 1)
                {
                    //Check for Null
                    if(this.single_form_id == null || this.single_form_id != this.filters['covid_form_entries.covid_form_id'][0].id)
                    {
                        //Fetch and Setup That Forms Fields
                        this.setupFormFilterFields();

                        //Show the Filter
                        this.$refs.customFormTableFilters.displayFilters();
                    }
                }
                else
                {
                    //Reset it
                    this.single_form_filters = null;
                    this.single_form_id = null;
                }
            },

            setupFormFilterFields(){
                //Send it
                this.$http.get('/custom-forms/filters', {covid_form_id: this.filters['covid_form_entries.covid_form_id'][0].id})
                    .then(response => {
                        //Setup the Data
                        if(response.data.filters.length > 0)
                        {
                            //setup the filters
                            this.single_form_filters = response.data.filters;
                            this.single_form_id = this.filters['covid_form_entries.covid_form_id'][0].id;
                        }
                        else
                        {
                            this.single_form_filters = null;
                            this.single_form_id = null;
                        }
                    })
                    .catch(err => {
                        //Alert the user
                        this.$toastr.error(err.response.data, 'Failed to Load Custom Form Filters');
                    })
                    .then(response => {
                        //Do something?
                    });
            },

            getFormFilterOptions(options){
                //Build it
                var select_options = [];

                //Keys
                var keys = Object.keys(options);

                //Loop
                for (var i = 0; i < keys.length; i++)
                {
                    select_options.push({id: keys[i], name: options[keys[i]]});
                }

                //SEnd it
                return select_options;
            },

            filtersCleared() {
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.customFormTableFilters.resetColVis(this.$refs.customFormEntryTable);
            },

            setUserFilterData(){
                //Call the Helper
                this.$refs.customFormsSavedFilters.setFilterParams(this.$refs.customFormEntryTable);
            },

            checkForAppliedFilter(){
                //Check if
                if(!this.allowFilterSaving || !this.appliedFilter)
                {
                    return;
                }

                //Call the helper
                this.$refs.customFormTableFilters.setColVis(this.$refs.customFormEntryTable);
            },

            exportTable(){
                //Loading indicator
                this.exportingTable = true;

                //Build the payload
                var payload = {
                    query: (this.$refs.customFormEntryTable.getRequestParams()).query,
                    orderBy: this.$refs.customFormEntryTable.options.orderBy.column,
                    ascending: this.$refs.customFormEntryTable.options.orderBy.ascending,
                    table: 'customFormEntryTable',
                    //columns: this.$refs.customFormEntryTable.columns,
                    columns: [
                        'id',
                        'custom_form_name',
                        'entry_status',
                        'person_name',
                        'EmployeeNumber',
                        'created_at',
                        'JobsiteName',
                        'signature_file_id'
                    ],
                    employee_id: this.employee_id,
                    filters: filterExtractor.extractFilters(this.filters),
                    //headings: this.options.headings
                };

                //Send it
                this.$http.get('/custom-forms/list?export=true', payload)
                    .then(response => {
                        //Open the new window
                        //window.open(response.data);

                        //EHS-1405, Changing this to the Queue, So it sends an
                        //email and then returns a success message
                        this.$toastr.success(response.data);
                    })
                    .catch(err => {
                        //Alert the user
                        this.$toastr.error(err.response.data, 'Failed to Export Custom Form Entries');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },

            pushSingleFilter(id){
                //Check for Null
                if(id == null)
                {
                    return;
                }

                //Push it
                for (var i = 0; i < this.single_form_filters.length; i++)
                {
                    if(this.single_form_filters[i].id === id)
                    {
                        //Set it
                        this.selected_single_form_filters.push(this.single_form_filters[i]);

                        //Push it
                        this.single_filter_add = null;
                    }
                }
            },

            removeSingleFilter(i){
                //Splice it out
                this.selected_single_form_filters.splice(i, 1);

                //Force update?
            }
        },

        mounted(){
            //Check for Staring Filters
            if(!this.dashboardFilters)
            {
                //Set it
                //this.$refs.incTableFilters.setFilters(this.dashboardFilters);

                //Check for a saved filter
                this.checkForAppliedFilter();

                //Set the Filter
                if(this.filterId)
                {
                    this.$refs.customFormsSavedFilters.setSavedFilter(this.filterId);
                }
            }

            //Check for Table Config
            if(this.tableConfig)
            {
                //Set it
                this.$refs.customFormTableFilters.setColVisFromSavedFilter(this.$refs.customFormEntryTable, this.tableConfig);
            }


        }
    }
</script>
